import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/entities/session/model/authStore';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { useMetadataStore } from '@/entities/metadata/model/metadataStore';
import { eventBus } from '@/shared/realtime/eventBus';
import { initI18n, normalizeLanguage } from '@/shared/i18n';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { useSystemHub } from '@/shared/realtime/systemHub';
import { useMetadataWatch } from '@/features/metadata-watch/useMetadataWatch';
import { usePresenceNotifications } from '@/features/presence';

/** Giriş sonrası: tercih hidrasyonu, dil, menü, metadata damgası, oturum ping'i, SessionExpired. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const language = usePrefsStore((s) => s.prefs.language);
  useSystemHub(status === 'authenticated');
  // Octo Lab'daki tasarım değişikliğini açık sekmelere duyurur (tarayıcı yenilemeye gerek kalmasın).
  useMetadataWatch();
  // Aynı kaydı başkası düzenlemeye başladı / kaydetti bildirimleri.
  usePresenceNotifications();

  useEffect(() => {
    void initI18n(normalizeLanguage(language));
  }, [language]);

  useEffect(() => {
    if (status !== 'authenticated' || !userId) return;
    // Taze girişte de tercihler sunucudan yüklenir ve kaydedilebilir hale gelir (userId bağlanır)
    if (usePrefsStore.getState().userId !== userId) void usePrefsStore.getState().hydrateFromServer(userId);
    const meta = useMetadataStore.getState();
    void meta.loadMenu(userId);
    void meta.loadIcons();
    void meta.checkMetadataStamp(userId, true);

    /*
     * Yetki değişince menü tazelensin.
     *
     * Sinyal sunucudan geliyordu ve olay veri yoluna basılıyordu ama HİÇ dinleyicisi
     * yoktu: yetki verilen kullanıcı, yeni ekranı görmek için çıkıp girmek zorunda
     * kalıyordu.
     */
    const onNav = () => {
      void useMetadataStore.getState().loadMenu(userId);
    };
    eventBus.on('RefreshNavigation', onNav);

    const ping = setInterval(() => {
      const { sessionId } = useAuthStore.getState();
      if (!sessionId) return;
      void apiClient
        .post(
          ENDPOINTS.UPDATE_LAST_ACTIVE,
          { SessionId: sessionId, UserId: userId, LastActiveTime: new Date().toISOString() },
          { octo: { expectSuccess: false } },
        )
        .catch(() => {});
    }, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void useMetadataStore.getState().checkMetadataStamp(userId);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(ping);
      document.removeEventListener('visibilitychange', onVisible);
      eventBus.off('RefreshNavigation', onNav);
    };
  }, [status, userId]);

  return <>{children}</>;
}
